// Inbox — one row per CONTACT (not per draft).
//
// We compose three queries and aggregate client-side: conversations +
// leads (for identity/status), messages (to find the most recent activity
// per conversation and render a preview), pending drafts (to show which
// contacts have a draft awaiting approval).
//
// Sort order: most recent message timestamp desc — same UX as a real DM
// inbox where new replies bump the contact to the top.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { Conversation, Lead, Message } from "../lib/types";
import { LeadStatusPill, EyebrowPill } from "../components/StatusPill";
import { InboxFilters, FilterKey } from "../components/InboxFilters";
import { EmptyState } from "../components/EmptyState";
import { MascotAvatar } from "../components/MascotAvatar";

interface InboxRow {
  conversation: Conversation;
  lead: Lead;
  lastMessage: Message | null;
  hasPendingDraft: boolean;
  // Used for sorting: timestamp of last message, or conversation start if no message yet.
  sortAt: string;
  // Computed: an inbound message arrived after the user last read this thread.
  // Only inbounds count — your own outbounds don't make the row look unread.
  unread: boolean;
}

export default function Inbox() {
  const [rows, setRows] = useState<InboxRow[]>([]);
  const [filter, setFilter] = useState<FilterKey>("pending");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);

    // 1. Every conversation we track + its lead.
    const { data: convs } = await supabase
      .from("conversations")
      .select(`*, leads (*)`)
      .limit(500);

    if (!convs || convs.length === 0) {
      setRows([]);
      setLoading(false);
      return;
    }

    const conversationIds = convs.map((c) => c.id);

    // 2. Recent messages — fetch a generous window then group client-side
    //    to find the latest message per conversation. Cheaper than N
    //    round-trips (one per convo) and easy to subscribe to via realtime.
    const [{ data: msgs }, { data: pendingDrafts }] = await Promise.all([
      supabase
        .from("messages")
        .select("id, conversation_id, content, direction, sent_at")
        .in("conversation_id", conversationIds)
        .order("sent_at", { ascending: false })
        .limit(2000),
      // 3. Conversations with at least one pending draft.
      supabase
        .from("drafts")
        .select("conversation_id")
        .eq("status", "pending"),
    ]);

    const lastMsgByConv = new Map<string, Message>();
    for (const m of msgs ?? []) {
      // First occurrence wins because we sorted desc.
      if (!lastMsgByConv.has(m.conversation_id)) {
        lastMsgByConv.set(m.conversation_id, m as Message);
      }
    }

    const pendingByConv = new Set<string>();
    for (const d of pendingDrafts ?? []) {
      pendingByConv.add(d.conversation_id as string);
    }

    // Compute unread: scan messages for the latest INBOUND per conversation,
    // compare with last_read_at. If the inbound is newer (or last_read_at is
    // null), the row is unread.
    const lastInboundByConv = new Map<string, Message>();
    for (const m of msgs ?? []) {
      if (m.direction !== "inbound") continue;
      if (!lastInboundByConv.has(m.conversation_id)) {
        lastInboundByConv.set(m.conversation_id, m as Message);
      }
    }

    const built: InboxRow[] = (convs as Array<Conversation & { leads: Lead }>).map((c) => {
      const last = lastMsgByConv.get(c.id) ?? null;
      const lastInbound = lastInboundByConv.get(c.id) ?? null;
      const unread = !!lastInbound && (!c.last_read_at || lastInbound.sent_at > c.last_read_at);
      return {
        conversation: c,
        lead: c.leads,
        lastMessage: last,
        hasPendingDraft: pendingByConv.has(c.id),
        sortAt: last?.sent_at ?? c.started_at ?? c.last_message_at,
        unread,
      };
    });

    built.sort((a, b) => b.sortAt.localeCompare(a.sortAt));
    setRows(built);
    setLoading(false);
  }

  async function toggleRead(conversationId: string, currentlyUnread: boolean) {
    // Toggle: unread → read sets last_read_at = now; read → unread sets
    // last_read_at = null so the latest inbound's sent_at wins the
    // comparison and the row resurfaces in Pending.
    const nextLastRead = currentlyUnread ? new Date().toISOString() : null;
    // Optimistic UI: flip first so the row visually responds, then persist.
    setRows((prev) =>
      prev.map((r) =>
        r.conversation.id === conversationId ? { ...r, unread: !currentlyUnread } : r,
      ),
    );
    await supabase
      .from("conversations")
      .update({ last_read_at: nextLastRead })
      .eq("id", conversationId);
  }

  useEffect(() => {
    load();
    // Subscribe to messages, drafts, AND conversations:
    //   - messages: new inbound bumps the contact to the top
    //   - drafts: a draft going pending/sending/sent flips the chip
    //   - conversations: last_read_at changes (mark-as-read from a
    //     decision on Conversation page) clear the unread state without
    //     waiting for the user to navigate
    const channel = supabase
      .channel("inbox-feed")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "messages" },
        () => load(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "drafts" },
        () => load(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "conversations" },
        () => load(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const filtered = useMemo(() => {
    switch (filter) {
      case "pending":
        // "Pending" = things you still need to deal with. A draft you've
        // already marked as read (or implicitly read by opening the thread)
        // leaves the pending list — find it again via "Tous".
        return rows.filter((r) => r.hasPendingDraft && r.unread);
      case "qualified":
        return rows.filter((r) => r.lead.current_status === "dm_qualified");
      case "booked":
        return rows.filter((r) => r.lead.current_status === "dm_call_booked");
      case "all":
      default:
        return rows;
    }
  }, [rows, filter]);

  const counts: Record<FilterKey, number> = {
    pending: rows.filter((r) => r.hasPendingDraft && r.unread).length,
    qualified: rows.filter((r) => r.lead.current_status === "dm_qualified").length,
    booked: rows.filter((r) => r.lead.current_status === "dm_call_booked").length,
    all: rows.length,
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border bg-bg-elevated/40 px-6 py-4 backdrop-blur">
        <div className="mb-3 flex items-center gap-3">
          <EyebrowPill>Inbox</EyebrowPill>
          <h2 className="font-display text-2xl font-semibold tracking-display">
            Drafts <span className="text-text-muted">à approuver</span>
          </h2>
        </div>
        <InboxFilters active={filter} onChange={setFilter} counts={counts} />
      </div>

      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="p-8 text-text-muted">Loading...</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title={filter === "pending" ? "Inbox zero 🎉" : "Aucun match"}
            subtitle={
              filter === "pending"
                ? "Pas de draft à approuver pour le moment. Tu peux respirer."
                : "Aucun contact ne correspond à ce filtre. Switch vers Tous pour voir l'historique."
            }
            vibe={filter === "pending" ? "celebratory" : "calm"}
          />
        ) : (
          <ul className="divide-y divide-border">
            {filtered.map((r) => {
              const preview = r.lastMessage
                ? `${r.lastMessage.direction === "inbound" ? "(Lead) " : "(Vous) "}${r.lastMessage.content}`
                : "Pas encore de message";
              return (
                <li
                  key={r.conversation.id}
                  className="group relative transition-colors hover:bg-bg-elevated/60"
                >
                  <Link
                    to={`/conversation/${r.conversation.id}`}
                    className="block px-6 py-4 pr-20"
                  >
                    <div className="flex items-start gap-3">
                      {/* Unread dot — colored when unread, transparent placeholder otherwise so name alignment stays put */}
                      <span
                        className={`mt-2 size-2 shrink-0 rounded-full ${
                          r.unread ? "bg-accent-400 ring-2 ring-accent-500/30" : "bg-transparent"
                        }`}
                        aria-label={r.unread ? "non lu" : "lu"}
                      />
                      <MascotAvatar size={36} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2.5">
                          <span
                            className={`truncate ${
                              r.unread ? "font-semibold text-text" : "font-medium text-text"
                            }`}
                          >
                            {r.lead?.full_name ?? "Lead inconnu"}
                          </span>
                          {r.lead && <LeadStatusPill status={r.lead.current_status} />}
                          {r.hasPendingDraft && (
                            <span className="rounded-full bg-accent-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent-300 ring-1 ring-accent-500/30">
                              draft
                            </span>
                          )}
                          <span className="ml-auto shrink-0 text-[11px] text-text-dim tabular-nums">
                            {new Date(r.sortAt).toLocaleString("fr-CA", {
                              timeZone: "America/Montreal",
                              hour: "2-digit",
                              minute: "2-digit",
                              day: "2-digit",
                              month: "short",
                            })}
                          </span>
                        </div>
                        <p
                          className={`mt-1 line-clamp-2 text-sm ${
                            r.unread ? "text-text" : "text-text-muted"
                          }`}
                        >
                          {preview}
                        </p>
                        {r.lead?.industry && (
                          <div className="mt-2 flex items-center gap-2 text-[11px] text-text-dim">
                            <span className="rounded bg-bg-elevated px-1.5 py-0.5 font-mono">
                              {r.lead.industry}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>

                  {/* Toggle read/unread. Always clickable in either direction:
                      unread → read marks last_read_at = now, read → unread
                      clears last_read_at so the row falls back into Pending
                      (since the latest inbound's sent_at then wins). */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleRead(r.conversation.id, r.unread);
                    }}
                    title={r.unread ? "Marquer comme lu" : "Marquer comme non lu (retour dans Pending)"}
                    className={`absolute right-4 top-1/2 -translate-y-1/2 rounded-md border px-2 py-1 text-[11px] transition-colors ${
                      r.unread
                        ? "border-accent-500/40 text-accent-300 hover:bg-accent-500/10"
                        : "border-border text-text-dim hover:border-accent-500/40 hover:text-accent-300"
                    }`}
                  >
                    {r.unread ? "✓ Lu" : "↺ Non lu"}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
